/**
 * EditContactModal — recruiters fix a lead's identity (name / phone / email)
 * straight from the profile header. Mirrors DisqualifyModal's bottom-sheet
 * dialog. PATCHes /contacts/:id with the raw values; the backend normalizes
 * the phone to E.164, lowercases the email, recomputes full_name, and enforces
 * the per-network phone/email uniqueness (a collision surfaces as a 409 here).
 * onSaved receives the server's canonical updated contact.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../services/api';
import { Contact } from '../types';
import { formatPhoneInput } from '../lib';

interface EditContactModalProps {
  contact: Contact;
  onClose: () => void;
  onSaved: (updated: Contact) => void;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

/** Best-effort split of a single full name into first / last for prefill. */
function splitName(full: string): { first: string; last: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length <= 1) return { first: parts[0] || '', last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

export default function EditContactModal({ contact, onClose, onSaved }: EditContactModalProps) {
  // Prefill first/last from the split columns; fall back to splitting full_name
  // for leads that only ever had a combined name.
  const seed =
    contact.first_name || contact.last_name
      ? { first: contact.first_name || '', last: contact.last_name || '' }
      : contact.full_name
      ? splitName(contact.full_name)
      : { first: '', last: '' };

  const [firstName, setFirstName] = useState(seed.first);
  const [lastName, setLastName] = useState(seed.last);
  const [phone, setPhone] = useState(formatPhoneInput(contact.phone_e164 || ''));
  const [email, setEmail] = useState(contact.email || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch(`/api/crm/contacts/${contact.id}`, {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        phone: phone.trim(),
        email: email.trim() || null,
      });
      onSaved(res.data?.contact);
    } catch (e: any) {
      const apiErr = e?.response?.data?.error;
      setError(apiErr || e?.response?.data?.detail || e.message || 'Could not save the changes.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-t-xl bg-white p-5 shadow-xl focus:outline-none dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Edit contact details"
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Edit contact details</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Update the lead's name, phone and email. Leave a field blank to clear it.
        </p>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputCls}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Last name</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Phone</label>
            <input
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              placeholder="123-456-7890"
              inputMode="tel"
              autoComplete="tel"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              inputMode="email"
              autoComplete="email"
              className={inputCls}
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
